import React, { useEffect, useState, Fragment } from 'react';
import { Row, Col, Button, message, Space, Typography, Spin, Form } from 'antd';
import { useForm } from 'react-hook-form';
import ListCard from 'Molecules/ListCard';
import HeadingChip from 'Molecules/HeadingChip';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getVendors } from '../ducks/actions'
import { removeVendor, bulkRemoveVendors, bulkHideVendors, syncAllFromNetsuite } from '../ducks/services'
import ActionButton from 'Molecules/ActionButton';
import Search from './components/Search';
import AllRoles from '../../../../routing/config/AllRoles';
import { allowed } from '../../../../routing/config/utils';
import { getListQueryItems } from 'Modules/Application/ducks/actions';
import { CloseCircleFilled, CheckCircleFilled, CheckCircleTwoTone, LoadingOutlined } from '@ant-design/icons';
import { Popup } from 'Atoms/Popup';
import FormGroup from 'Molecules/FormGroup';
import dayjs from 'dayjs';
import { useTranslate } from 'Translate';

const antIcon = <LoadingOutlined spin />;
const { Title } = Typography;

export default (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { control, formState: { errors }, handleSubmit, setValue } = useForm();
  const vendors = useSelector((state) => state.inventory.vendors);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchVal, setSearchVal] = useState(null);
  const allowedEdit = allowed([AllRoles.INVENTORY.VENDOR], 'write');
  const allowedDelete = allowed([AllRoles.INVENTORY.VENDOR], 'delete');
  const globalListQuery = useSelector((state) => state.global.listQueryItems);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const i18n = useTranslate();
    const { t } = i18n;

  const colName = [
    {
      title: t("General.code"),
      dataIndex: 'code',
      key: 'code',
      className: 'enable-click',
      sorter: true,
      render: (text, record) => <span>{text} {record?.reference && text && <CheckCircleTwoTone twoToneColor="#52c41a" /> }</span>
    },
    {
      title: t("Outlet.name"),
      dataIndex: 'name',
      key: 'name',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("Inventory.is_hide"),
      dataIndex: 'is_hide',
      key: 'is_hide',
      className: 'enable-click',
      sorter: true,
      render: (text) => <span>{text === "1" ? <CheckCircleFilled className='c-success' style={{ fontSize: '20px' }} /> : <CloseCircleFilled className='c-danger' style={{ fontSize: '20px' }} />}</span>
    },
    {
      title: t("General.status"),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (text, record) =>
        <div className='flex align-items-center'>
          <span className={text === "Active" ? "c-success" : "c-danger"}>
            {t(`General.${text?.toLowerCase()}`)}
          </span>
          {
            allowedEdit &&
            <Button type="link" onClick={() => onAction(record.id, text)}>
              {text === "Active" ? `(${t("General.deactivate")})` : `(${t("General.activate")})`}
            </Button>
          }
        </div>
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) =>
        <ActionButton
          title="vendor"
          btnAction1={() => navigate(`/inventory/vendor/${record.id}`)}
          recordId={record.id}
          onRemove={allowedDelete && onRemove}
          setLoading={props.setLoading}
          loading={props.loading}
        />
    }
  ]

  const addNew = () => navigate('/inventory/vendor/add');

  const btnList = [
    {
      title: t("Confirm.remove_select_vendors"),
      text: t("General.bulk_remove"),
      classes: 'red-btn text-white attendance-success',
      permit: true,
      action: () => onBulkRemove(),
    },
    {
      title: t("Confirm.hide_selected_vendors"),
      text: t("Inventory.bulk_hide"),
      classes: 'pending-outlined-btn',
      permit: true,
      action: () => onBulkHide(),
    },
    {
      text: t("Inventory.sync_all_vendors"),
      classes: 'green-btn',
      action: () => setVisible(true)
    },
    {
      text: `+ ${t("New.vendor")}`,
      action: () => addNew(),
    },
  ];

  useEffect(() => {
    if(!globalListQuery.vendorListQuery) {
      dispatch(getVendors(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('vendorListQuery', 1, limit, '', '', null, props.setLoading));
    } else {
      const {page, limit, sortOrder, sortColKey, searchValue} = globalListQuery.vendorListQuery
      dispatch(getVendors(page, limit, sortOrder, sortColKey, searchValue, props.setLoading));
      setPage(page);
      setLimit(limit);
      setSearchVal(searchValue);
    }
  }, []);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onAction = async (vendorId, status) => {
    props.setLoading(true);

    const payload = {
      vendor_id: vendorId,
      status_event: status === 'Active' ? 'deactivate' : 'activate'
    }

    await removeVendor(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("Success.update_vendor_status"));
        setTimeout(() => dispatch(getVendors(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('vendorListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onRemove = async (id) => {
    props.setLoading(true);

    const payload = {
      vendor_id: id,
      status_event: 'remove'
    }

    await removeVendor(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("Success.remove_vendor"));
        setTimeout(() => dispatch(getVendors(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('vendorListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onBulkRemove = async () => {
    props.setLoading(true);

    const payload = {
      vendors: JSON.stringify(selectedRowKeys)
    }

    await bulkRemoveVendors(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        rowSelection.onChange([]);
        message.success(t("Success.remove_vendors"));
        setPage(1);
        setSearchVal(null);
        setTimeout(() => dispatch(getVendors(1, limit, '', '', null, props.setLoading)), 500);
        dispatch(getListQueryItems('vendorListQuery', 1, limit, '', '', null, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onBulkHide = async () => {
    props.setLoading(true);

    const payload = {
      vendors: JSON.stringify(selectedRowKeys)
    }

    await bulkHideVendors(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        rowSelection.onChange([]);
        message.success(t("Success.hide_vendors"));
        setPage(1);
        setSearchVal(null);
        setTimeout(() => dispatch(getVendors(1, limit, '', '', null, props.setLoading)), 500);
        dispatch(getListQueryItems('vendorListQuery', 1, limit, '', '', null, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter.order) {
      dispatch(getVendors(pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading));
      dispatch(getListQueryItems('vendorListQuery', pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading))
    } else {
      dispatch(getVendors(pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading));
      dispatch(getListQueryItems('vendorListQuery', pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading))
    }
  }

  const onSearch = (search) => {
    setPage(1);
    if (search) {
      let searching = {};
      searching = {
        code: search.code
      };
      setSearchVal(searching);
      dispatch(getVendors(1, limit, '', '', searching, props.setLoading));
      dispatch(getListQueryItems('vendorListQuery', 1, limit, '', '', searching, props.setLoading))
    } else {
      setSearchVal(null);
      dispatch(getVendors(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('vendorListQuery', 1, limit, '', '', null, props.setLoading))
    }
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click"))
          navigate(`/inventory/vendor/${record.id}`)
      },
    };
  };

  const onSyncAll = async (val) => {
    setLoading(true);

    const payload = {
      date_from: val?.date_from ? dayjs(val?.date_from).format("MM/DD/YYYY") : "",
      date_to: val?.date_to ? dayjs(val?.date_to).format("MM/DD/YYYY") : ""
    }

    await syncAllFromNetsuite(payload).then(res => {
      setLoading(false);
      if (res.data.code === 200) {
        setVisible(false);
        message.success(res.data.message);
      } else {
        message.error(res.data.message)
      }
    }).catch(e => {
      setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"))
    })
  };

  const disabledDates = (current) => {
    return current && current > dayjs().endOf('day');
  }

  const formFields = [
    {
      name: 'date_from',
      label: t("General.date_from"),
      req: true,
      placeholder: t("General.please_select"),
      type: 'date',
      twocol: false,
      colWidth: '0 1 100%',
      reqmessage: 'Date From is Required'
    },
    {
      name: 'date_to',
      label: t("General.date_to"),
      req: true,
      placeholder: t("General.please_select"),
      type: 'date',
      twocol: false,
      colWidth: '0 1 100%',
      reqmessage: 'Date To is Required',
      disabledDate: disabledDates
    },
  ];

  const popup = {
    closable: false,
    visibility: visible,
    content: <Spin indicator={antIcon} size="large" spinning={loading}>
      <Form layout="vertical" onFinish={handleSubmit(onSyncAll)}>
        <Space direction='vertical' size={30} className='w-100'>
          <Title level={4} className='m-0'>
            {t("General.select_date_range")}
          </Title>
          <Row gutter={[10, 10]}>
            {formFields.map((item, idx) => (
              <Fragment key={idx}>
                <FormGroup item={item} control={control} errors={errors} />
              </Fragment>
            ))}
          </Row>
          <Row gutter={10} justify={'center'}>
            <Col span={12}><Button size='large' danger type="primary" className='w-100' onClick={() => setVisible(false)}>{t("General.cancel")}</Button></Col>
            <Col span={12}><Button size='large' type="primary" className='green-btn w-100' htmlType='submit'>{t("General.confirm")}</Button></Col>
          </Row>
        </Space>
      </Form>
    </Spin>,
    width: 650,
    onCancel: () => {
      setVisible(false);
      setValue('date_from', '');
      setValue('date_to', '');
    },
  };

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <HeadingChip title={t("General.vendors")} btnList={allowedEdit && btnList} btnHidden={selectedRowKeys.length <= 0} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            Search={Search}
            onSearch={onSearch}
            searchVal={globalListQuery.vendorListQuery?.searchValue}
            onRow={onClickRow}
            // listClass="nopad"
            ListData={vendors?.list?.map(x => ({ ...x, key: x.id }))}
            ListCol={colName}
            rowSelection={allowedEdit && rowSelection}
            onChange={onTableChange}
            pagination={{
              total: vendors?.total_count,
              current: page,
              pageSize: limit
            }}
          />
        </Col>
      </Row>
      <Popup {...popup} />
    </>
  )
}